import { Drawer } from '@billy/design-system'
import { useTranslation } from 'react-i18next'

import { TransactionOriginatorType } from '@features/transactions/enums/transactionOriginatorType'
import { useVatDeclaration } from '@features/vatDeclarations/hooks/useVatDeclaration'
import { VatPayment } from '@features/vatDeclarations/query-api'

import { useTransactionStaticActions } from '../../../../hooks/useTransactionStaticActions'
import { useTransactionDrawerContext } from '../../contexts/TransactionDrawerContext'
import { BookingsSection } from '../../elements/BookingsSection/BookingsSection'
import { DetailsSection } from '../../elements/DetailsSection'
import { HeroSection } from '../../elements/HeroSection'
import { Separator } from '../../elements/Separator'
import { TransactionDrawerSkeleton } from '../../elements/TransactionDrawerSkeleton'

interface VatReturnPaymentViewProps {
  vatReturnPayment: VatPayment
}

export const VatReturnPaymentView = ({ vatReturnPayment }: VatReturnPaymentViewProps) => {
  const { t } = useTranslation()
  const { isVoidedEntry, transactionNo, transaction, transactionId, originatorCurrencyId } =
    useTransactionDrawerContext()

  const { amount, entryDate, id, returnId } = vatReturnPayment

  const { vatDeclaration, isLoading } = useVatDeclaration({ vatDeclarationId: returnId })
  const { handleSelect, items, modals } = useTransactionStaticActions({
    isVoided: isVoidedEntry,
    originatorId: id,
    originatorType: TransactionOriginatorType.SalesTaxPayment,
    transactionId,
  })

  const { createdTime } = transaction || {}

  if (isLoading || !vatDeclaration) {
    return <TransactionDrawerSkeleton transactionNo={transactionNo} />
  }

  const period = vatDeclaration.periodText || ''
  const status = { color: 'green', label: t('vat_declarations.table.category.paid') } as const

  return (
    <>
      <Drawer.Header
        actions={items}
        closable
        onAction={handleSelect}
        title={t('ledger.drawer_title', { number: transactionNo })}
      />
      <Drawer.Body withPadding={false} gap="0">
        <HeroSection
          amount={amount}
          currency={originatorCurrencyId}
          entryDate={entryDate}
          source={period}
          status={status}
        />
        <Separator />
        <BookingsSection originatorCurrency={originatorCurrencyId} />
        <Separator />
        <DetailsSection createdTime={createdTime} />
        <Separator />
      </Drawer.Body>
      {modals}
    </>
  )
}
